import { MainApi } from 'api/MainApi'
import clsx from 'clsx'
import { ReactComponent as CalculateRouteSvg } from 'images/icons/calculate_route.svg'
import { ReactComponent as SuccessSvg } from 'images/icons/success.svg'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MissionActions } from 'store/actions/mission'
import {
  selectMissionCalculatingStatus,
  selectMissionFinishPoint,
  selectMissionInfo,
  selectMissionReadyForCalculation,
  selectMissionStartPoint,
} from 'store/selectors/mission'
import { MISSION_CALCULATING_STATUSES, TRANSFER_TYPE } from 'utils/constants'
import { ICleaningZone, IRestrictZone } from 'utils/interfaces'

import MissionPointTransferItem from './TransferItems/MissionPointTransferItem'
import StartFinishPointItem from './TransferItems/StartFinishPointItem'
import TransferRouteItem from './TransferItems/TransferRouteItem'
import CleaningZoneItem from './ZoneItem/CleaningZoneItem'
import RestrictedZoneItem from './ZoneItem/RestrictedZoneItem'

import style from './ViewZonesMode.module.css'

type ViewZonesModeProps = {
  cleaningZones: Array<ICleaningZone>
  restrictZones: Array<IRestrictZone>
  setCalculationMode?: (mode: 'all' | 'single' | undefined) => void
  hideActions?: boolean
  hideCleaningType?: boolean
}

const formatTime = (seconds: number) => {
  if (seconds > 0 && seconds < 60) {
    return '1m' // Was '0h:1m'
  }
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) {
    return `${h}h:${m}m`
  }
  return `${m}m`
}

const ViewZonesMode = (props: ViewZonesModeProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const missionStartPoint = useSelector(selectMissionStartPoint)
  const missionFinishPoint = useSelector(selectMissionFinishPoint)
  const missionInfo = useSelector(selectMissionInfo)
  const calculatingProgress = useSelector(selectMissionCalculatingStatus)
  const readyForCalculation = useSelector(selectMissionReadyForCalculation)
  const onCalculateRouteClicked = async () => {
    if (!readyForCalculation || calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING) {
      return
    }
    try {
      await dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.CALCULATING))
      await MainApi.startCalculatingRoutes(missionInfo?.id || -1)
      if (props.setCalculationMode) {
        props.setCalculationMode('all') // Use prop function
      }
    } catch (error) {
      await dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.ERROR))
    }
  }

  const calculationStatusClass = useMemo(() => {
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.ERROR) return style.error
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING) return style.calculating
    if (calculatingProgress === MISSION_CALCULATING_STATUSES.OK) return style.ok
    if (readyForCalculation) return style.warn
    return ''
  }, [calculatingProgress, readyForCalculation])

  const cleaningZonesList = useMemo(() => {
    return (
      <>
        {props.cleaningZones.map((zone, index) => (
          <div key={zone.properties.zone_id}>
            {index !== 0 && (
              <TransferRouteItem
                type={TRANSFER_TYPE.MIDDLE}
                zoneIdToTransfer={zone.properties.zone_id}
                hideActions={props.hideActions}
              />
            )}
            <CleaningZoneItem
              zone={zone}
              number={index + 1}
              hideActions={props.hideActions}
              setCalculationMode={props.setCalculationMode || (() => {})}
            />
          </div>
        ))}
      </>
    )
  }, [props.cleaningZones, props.hideActions])

  return (
    <div className={style.tableWrapper}>
      <div className={style.missionStatsContainer}>
        <div className={style.missionStatsGrid}>
          <div className={style.statCard}>
            {/* <div className={clsx(style.statIconWrapper, style.area)}>
              <AreaSvg className={style.statIcon} />
            </div> */}
            <div className={style.statLabel}>Coverage</div>
            <div className={style.statValue}>
              {Math.round(missionInfo?.overall_cleaned_area || 0)}m<sup>2</sup>
            </div>
          </div>
          <div className={style.statCard}>
            {/* <div className={clsx(style.statIconWrapper, style.length)}>
              <LengthSvg className={style.statIcon} />
            </div> */}
            <div className={style.statLabel}>Length</div>
            <div className={style.statValue}>{Math.round(missionInfo?.overall_length || 0)}m</div>
          </div>
          <div className={style.statCard}>
            {/* <div className={clsx(style.statIconWrapper, style.time)}>
              <TimeSvg className={style.statIcon} />
            </div> */}
            <div className={style.statLabel}>Exp Time</div>
            <div className={style.statValue}>{formatTime(missionInfo?.overall_execution_time || 0)}</div>
          </div>
          {/* New Calculation Status Card */}
          <div
            className={clsx(style.statCard, style.statCardRow, readyForCalculation ? style.clickable : style.disabled)}
            onClick={onCalculateRouteClicked}
          >
            <div className={clsx(style.statIconWrapper, style.status, calculationStatusClass)}>
              {calculatingProgress === MISSION_CALCULATING_STATUSES.OK ? (
                <SuccessSvg className={style.statIcon} />
              ) : (
                <CalculateRouteSvg className={style.statIcon} />
              )}
            </div>
            <div className={style.statContent}>
              <div className={style.statLabel}>
                {calculatingProgress !== MISSION_CALCULATING_STATUSES.OK ? 'Press for' : 'Status'}
              </div>
              <div className={style.statValue} style={{ fontSize: '16px' }}>
                {calculatingProgress === MISSION_CALCULATING_STATUSES.OK
                  ? 'Ready'
                  : calculatingProgress === MISSION_CALCULATING_STATUSES.CALCULATING
                  ? 'Calculation'
                  : 'Calculation'}
              </div>
            </div>
          </div>
        </div>
      </div>
      {props.cleaningZones.length || props.restrictZones.length || missionStartPoint || missionFinishPoint ? (
        <div className={style.table}>
          <div className={props.hideCleaningType ? style.tableHeaderShort : style.tableHeader}>
            <div></div>
            <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>{t('view_zones.zone_number')}</div>
            <div className={clsx(style.tableHeaderItemSmall, 'text-16-400')}>{t('view_zones.type')}</div>
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('view_zones.name')}</div>
            {!props.hideCleaningType && (
              <div className={clsx(style.tableHeaderItem, 'text-16-400')}>{t('view_zones.cleaning_type')}</div>
            )}
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>
              {props.hideCleaningType ? 'Length/Coverage' : 'Length/ Coverage'}
            </div>
            <div className={clsx(style.tableHeaderItem, 'text-16-400')}>Exp time</div>
            <div></div>
          </div>
          {missionStartPoint && (
            <StartFinishPointItem
              type={TRANSFER_TYPE.START}
              missionPoint={missionStartPoint}
              hideActions={props.hideActions}
            />
          )}
          <MissionPointTransferItem type={TRANSFER_TYPE.START} hideActions={props.hideActions} />
          {cleaningZonesList}
          <MissionPointTransferItem type={TRANSFER_TYPE.FINISH} hideActions={props.hideActions} />
          {missionFinishPoint && (
            <StartFinishPointItem
              type={TRANSFER_TYPE.FINISH}
              missionPoint={missionFinishPoint}
              hideActions={props.hideActions}
            />
          )}

          {props.restrictZones.map((zone, index) => (
            <RestrictedZoneItem
              key={index}
              zone={zone as IRestrictZone}
              number={index + props.cleaningZones.length + 1}
              hideActions={props.hideActions}
            />
          ))}
        </div>
      ) : (
        <div className={style.emptyWrapper}>
          <div className={style.emptyTitle}>{t('view_zones.no_zones_title')}</div>
          <div className={style.emptyDescription}>{t('view_zones.no_zones_description')}</div>
        </div>
      )}
    </div>
  )
}

export default ViewZonesMode
